import { DEFAULT_LOCALE, type Locale } from "./locale";

/**
 * The site's translation lookup, in place of i18next.
 *
 * i18next was doing three things here: holding the bundles, tracking a
 * "current language", and re-rendering React when that language changed. Only
 * the first is still wanted. The language is now decided by the proxy and
 * baked into the prerendered tree (see lib/i18n/locale.ts), so a mutable
 * global "current language" is not just unnecessary — it is a hazard: the
 * module is a singleton shared by every render on the server, and one request
 * changing it would change the language of a page being rendered next to it.
 *
 * So lookups take the locale explicitly and nothing here is stateful except
 * the bundles themselves, which are identical for every request. That also
 * takes react-i18next and i18next out of the client bundle entirely (~52 KB)
 * for a lookup that is, in the end, a dotted path into a JSON object.
 */

type Bundles = Record<string, unknown>;

const registry: Record<Locale, Bundles> = { th: {}, en: {} };

export type TranslateOptions = Record<string, unknown> & {
  /** Return the raw node instead of a string — used by the legal pages, whose
   *  content is an array of sections rather than a single line. */
  returnObjects?: boolean;
};

/**
 * Merge a route's namespaces into the shared bundles.
 *
 * Call this at MODULE SCOPE from a route's i18n module (see
 * app/(site)/_i18n/*), never from inside a component: every module in a chunk
 * is evaluated before React renders any of it, so a module-scope call is
 * guaranteed to land before the first lookup.
 *
 * A namespace is overwritten rather than kept. This used to be `??=`, on the
 * reasoning that two routes registering the same slice should be a no-op — but
 * an assignment is not a rebuild, so first-wins bought nothing and cost a
 * whole class of bug: in dev, editing a locale file re-evaluates this module
 * with the new copy, `??=` sees a namespace already there and drops it, and
 * every key added since the tab was opened renders as its own dotted name
 * until a full reload. Overwriting makes an edit land. Two routes registering
 * the same slice still works — they pass the same imported object.
 */
export function registerNamespaces(bundles: { en: Bundles; th: Bundles }) {
  for (const [locale, slices] of Object.entries(bundles) as [Locale, Bundles][]) {
    for (const [namespace, value] of Object.entries(slices)) {
      registry[locale][namespace] = value;
    }
  }
}

/** Walk a dotted key (`"home.hero.lead1"`) into the bundles. */
function lookup(locale: Locale, key: string): unknown {
  let node: unknown = registry[locale];
  for (const part of key.split(".")) {
    if (node === null || typeof node !== "object") return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return node;
}

/** i18next's `{{name}}` syntax, which is all the locale files actually use. */
function interpolate(text: string, options?: TranslateOptions) {
  if (!options) return text;
  return text.replace(/\{\{(\w+)\}\}/g, (match, name: string) =>
    name in options ? String(options[name]) : match,
  );
}

/**
 * What a key that resolves to nothing renders as.
 *
 * It used to be the key itself in every environment, on the reasoning that a
 * miss would be caught in review. One was not: `home.mocks.tiers.perks.
 * birthdayGift` (the perk was filed one level up, beside `perks` rather than
 * inside it) shipped and printed its own dotted path into three rows of the
 * loyalty mock — inside a card that crops all but its left third, so no one
 * saw it, while every crawler read it as page copy.
 *
 * So the two environments want opposite things and now get them. In
 * development the key is still what renders, loudly, because a visible
 * `home.a.b.c` and an error in the console are how the miss gets found. In
 * production it renders as nothing: a gap in the layout is a smaller failure
 * than a debug string published as content, and it cannot be quoted back at us
 * by an answer engine.
 *
 * This is a net, not a fix. A miss is still a bug — see the console error.
 */
function missing(key: string) {
  if (process.env.NODE_ENV !== "production") {
    console.error(`[i18n] missing translation key: ${key}`);
    return key;
  }
  return "";
}

export function translate(
  locale: Locale,
  key: string,
  options?: TranslateOptions,
): string {
  const value = lookup(locale, key) ?? lookup(DEFAULT_LOCALE, key);

  if (options?.returnObjects) {
    // Callers that ask for objects cast the result themselves; the signature
    // stays `string` so the ~500 ordinary call sites need no change.
    return (value ?? missing(key)) as unknown as string;
  }

  return typeof value === "string" ? interpolate(value, options) : missing(key);
}

export type Translate = (key: string, options?: TranslateOptions) => string;

/** Bind a locale once, for a component tree that renders in one language. */
export function createTranslator(locale: Locale): Translate {
  return (key, options) => translate(locale, key, options);
}
